import { labels, clusters } from "../utils/globals.js";
import { get } from "svelte/store";
import {
  BufferAttribute,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Vector2,
  ShaderMaterial,
  Mesh,
  DoubleSide,
  TextureLoader,
  NearestFilter,
  LinearFilter,
  CanvasTexture,
  Color,
} from "three";
import fs from "../glsl/thumbs.frag";
import vs from "../glsl/thumbs.vert";
import b_fs from "../glsl/bubble_thumbs.frag";
import b_vs from "../glsl/bubble_thumbs.vert";
import { rgbToFloats } from "../utils/utils.js";

export default class Thumbnails extends Mesh {
  constructor(texture, mask) {
    // create the blueprint mesh
    let s = 15;
    let vertices = [-s, -s, 0, s, -s, 0, s, s, 0, -s, s, 0];
    let uvs = [0, 0, 1, 0, 1, 1, 0, 1];
    const geometry = new InstancedBufferGeometry();
    geometry.setAttribute(
      "position",
      new BufferAttribute(new Float32Array(vertices), 3)
    );
    geometry.setAttribute("uv", new BufferAttribute(new Float32Array(uvs), 2));
    geometry.setIndex(
      new BufferAttribute(new Uint32Array([0, 1, 2, 0, 2, 3]), 1)
    );

    //create the instances
    let instanceCount =
      get(clusters).reduce((accumulator, cluster) => {
        return accumulator + cluster.nodes.length;
      }, 0) + get(clusters).length;

    let clusterNodes = new Float32Array(instanceCount);
    let clusterId = new Float32Array(instanceCount);
    let sceondaryClusterId = new Float32Array(instanceCount);
    let offsets = new Float32Array(instanceCount * 3);
    let tsne = new Float32Array(instanceCount * 3);
    let colors = new Float32Array(instanceCount * 3);
    let uvOffset = new Float32Array(instanceCount * 2);
    let uvScales = new Float32Array(instanceCount * 2);

    //iterators
    let clusterNodeIterator = 0;
    let clusterIterator = 0;
    let secondaryClusterIterator = 0;
    let offsetIterator = 0;
    let tsneIterator = 0;
    let colorsIterator = 0;
    let uvOffsetIterator = 0;
    let uvScalesIterator = 0;

    let cols = 39;
    let rows = 39;
    let uvScale = new Vector2(1 / cols, 1 / rows);
    let id = 0;
    const unit = 1 / 39 / 64;

    get(clusters).forEach((cluster, i) => {
      //adds the cluster nodes (doux jésus que c'est vilain...)
      clusterId[clusterIterator++] = cluster.uid;
      sceondaryClusterId[secondaryClusterIterator++] = cluster.uid;
      clusterNodes[clusterNodeIterator++] = 1;
      let col = rgbToFloats(cluster.color);
      colors[colorsIterator++] = col[0];
      colors[colorsIterator++] = col[1];
      colors[colorsIterator++] = col[2];
      offsets[offsetIterator++] = cluster.x;
      offsets[offsetIterator++] = cluster.y;
      offsets[offsetIterator++] = 0;
      tsne[tsneIterator++] = cluster.x;
      tsne[tsneIterator++] = cluster.y;
      tsne[tsneIterator++] = 0;
      uvOffset[uvOffsetIterator++] = 0;
      uvOffset[uvOffsetIterator++] = 0;
      uvScales[uvScalesIterator++] = 0;
      uvScales[uvScalesIterator++] = 0;
      //add the nodes
      cluster.nodes.forEach((node) => {
        let x = node.data.uvId % cols;
        let y = rows - ~~(node.data.uvId / cols);
        //store the cluster id
        clusterId[clusterIterator++] = cluster.uid;
        //store the secondary cluster id
        let sid = cluster.uid;
        if (node.data.stats.length > 1) {
          //picks the second most imoprtant category of the node
          // sid = node.data.stats[1].cat;
        }
        sceondaryClusterId[secondaryClusterIterator++] = sid;

        //store whether this instance is a cluster
        clusterNodes[clusterNodeIterator++] = 0;

        //stores the cluster color
        colors[colorsIterator++] = node.data.col[0];
        colors[colorsIterator++] = node.data.col[1];
        colors[colorsIterator++] = node.data.col[2];

        //distribute as grid
        offsets[offsetIterator++] = node.data.x;
        offsets[offsetIterator++] = node.data.y;
        offsets[offsetIterator++] = 0;

        //adds a tsne destination
        tsne[tsneIterator++] = node.data.x;
        tsne[tsneIterator++] = node.data.y;
        tsne[tsneIterator++] = 0;

        uvOffset[uvOffsetIterator++] = x * uvScale.x + unit;
        uvOffset[uvOffsetIterator++] = y * uvScale.y - uvScale.y + unit;

        uvScales[uvScalesIterator++] = uvScale.x - 2 * unit;
        uvScales[uvScalesIterator++] = uvScale.y - 2 * unit;
      });
    });

    //create buffers
    geometry.setAttribute(
      "clusterNode",
      new InstancedBufferAttribute(clusterNodes, 1)
    );
    geometry.setAttribute(
      "clusterId",
      new InstancedBufferAttribute(clusterId, 1)
    );
    geometry.setAttribute(
      "sceondaryClusterId",
      new InstancedBufferAttribute(sceondaryClusterId, 1)
    );
    geometry.setAttribute("offset", new InstancedBufferAttribute(offsets, 3));
    geometry.setAttribute("color", new InstancedBufferAttribute(colors, 3));
    geometry.setAttribute(
      "uvOffset",
      new InstancedBufferAttribute(uvOffset, 2)
    );
    geometry.setAttribute("uvScale", new InstancedBufferAttribute(uvScales, 2));
    geometry.setAttribute("tsne", new InstancedBufferAttribute(tsne, 3));

    //material
    let material = new ShaderMaterial({
      uniforms: {
        pie: { type: "t", value: null },
        tex: { type: "t", value: texture },
        msk: { type: "t", value: mask },
        cluster: { value: -1 },
        fade: { value: 0 },
        tween: { value: 0 },
        noise: { value: 0 },
        time: { value: 0 },
      },
      vertexShader: vs,
      fragmentShader: fs,
      side: DoubleSide,
      transparent: true,
      depthTest: false,
    });

    //bubbles :
    // the material used to isolate a cluster and render it as gradient circles
    // to perform the isoline computation ( se isoline )
    let bubbles = new ShaderMaterial({
      uniforms: {
        upscale: { value: 6 },
        cluster: { value: -1 },
        feather: { value: new Vector2(0.65, 0.95) },
        zoom: { value: 1 },
        alpha: { value: 1 },
        invert: { value: 1 },
        time: { value: 0 },
      },
      vertexShader: b_vs,
      fragmentShader: b_fs,
      depthTest: false,
      transparent: true,
    });
    super(geometry, material);

    texture.needsUpdate = true;
    mask.needsUpdate = true;
    if (material.uniforms.pie !== undefined) {
      material.uniforms.pie.value = this.makePieCharts(texture);
    }

    this.geometry.computeBoundingSphere();
    this.geometry.boundingSphere.radius = 100000;
    this.renderOrder = 20;

    this.pieChartsMaterial = material;
    this.bubblesMaterial = bubbles;
    this.init();
  }

  setPieChartsMaterial() {
    this.material = this.pieChartsMaterial;
  }

  setBubblesMaterial(clusterId = 0, zoom = 1) {
    this.bubblesMaterial.uniforms.cluster.value = ~~clusterId;
    this.bubblesMaterial.uniforms.zoom.value = zoom;
    this.bubblesMaterial.needsUpdate = true;
    this.material = this.bubblesMaterial;
  }

  set tween(v) {
    this.material.uniforms.tween.value = v;
  }
  get tween() {
    return this.material.uniforms.tween.value;
  }
  set startTime(v) {
    startTime = v;
  }
  set time(v) {
    this.material.uniforms.time.value = Math.max(0, v);
  }
  get time() {
    return this.material.uniforms.time.value;
  }
  set noise(v) {
    this.material.uniforms.noise.value = Math.max(0, v);
  }
  get noise() {
    return this.material.uniforms.noise.value;
  }

  set cluster(v) {
    this.material.uniforms.cluster.value = v;
  }
  get cluster() {
    return this.material.uniforms.cluster.value;
  }

  makePieCharts(t) {
    let cols = 39;
    let rows = 39;
    let tilesize = 64;
    let radius = tilesize / 2;

    let canvas = document.createElement("canvas");
    canvas.width = t.image.width;
    canvas.height = t.image.height;

    let ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    get(clusters).forEach((cluster) => {
      cluster.nodes.forEach((node) => {
        const id = node.data.uvId;
        let x = id % cols;
        let y = rows - ~~(id / cols);

        x = x * tilesize + radius;
        y = t.image.height - y * tilesize + radius;

        const stats = node.data.stats;
        const sum = stats.reduce(
          (accumulator, stat) => accumulator + stat.val,
          0
        );
        let angle = 0;
        stats.forEach((stat) => {
          const delta = (stat.val / sum) * Math.PI * 2;

          const _labels = get(labels);
          ctx.fillStyle = _labels[stat.cat].color;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(
            x + Math.cos(angle) * radius,
            y + Math.sin(angle) * radius
          );
          ctx.arc(x, y, radius, angle, angle + delta);
          ctx.lineTo(x, y);
          ctx.fill();

          angle += delta;
        });

        ctx.strokeStyle = "#FFF";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.stroke();
      });
    });

    //
    ctx.fillStyle = "#F00";
    ctx.fillRect(0, canvas.height - 1, 1, 1);
    return new CanvasTexture(canvas);
  }

  wave() {
    this.time = (performance.now() - startTime) * 0.0005;
  }

  init() {
    let vertices = [];
    get(clusters).forEach((cluster) => {
      vertices.push(cluster.body.pos.x, cluster.body.pos.y, 0);
      cluster.bodies.forEach((body) => {
        vertices.push(body.pos.x, body.pos.y, 0);
      });
    });
    this.geometry.setAttribute(
      "offset",
      new InstancedBufferAttribute(new Float32Array(vertices), 3)
    );
    this.geometry.needsUpdate = true;
  }

  update(ratio) {
    let vertices = this.geometry.getAttribute("offset").array;
    const friction = 0.25;
    let uid = 0;
    get(clusters).forEach((cluster) => {
      // vertices[uid] += (cluster.body.pos.x - vertices[uid++]) * friction;
      // vertices[uid] += (cluster.body.pos.y - vertices[uid++]) * friction;
      // vertices[uid++] = 0;
      vertices[uid++] = cluster.body.pos.x;
      vertices[uid++] = cluster.body.pos.y;
      vertices[uid++] = 0;
      cluster.bodies.forEach((body) => {
        // vertices[uid] += (body.pos.x - vertices[uid++]) * friction;
        // vertices[uid] += (body.pos.y - vertices[uid++]) * friction;
        vertices[uid++] = body.pos.x;
        vertices[uid++] = body.pos.y;
        vertices[uid++] = 0;
      });
    });
    this.geometry.setAttribute(
      "offset",
      new InstancedBufferAttribute(new Float32Array(vertices), 3)
    );
    this.geometry.needsUpdate = true;
    if (this.material.uniforms.fade) this.material.uniforms.fade.value = ratio;
  }
}
